const convertStoredAges = months => months > 24 ? months / 12 : months;
const convertStoredAgeUnits = months => months > 24 ? 'years' : 'months';
const upperAgeLimit = age_units => age_units?.toLowerCase() === 'months' ? 24 : 100;
const aboveApiAgeTopLimit = (age_count, age_unit) => age_count > upperAgeLimit(age_unit);
const convertApiAgeRangeToUnits = apiAge => {
  const min_age = parseInt(apiAge.split('...')[0], 10);
  const max_age = parseInt(apiAge.split('...')[1], 10) - 1;
  return [min_age ? `${convertStoredAges(min_age)}` : '', min_age ? convertStoredAgeUnits(min_age) : '', max_age ? `${convertStoredAges(max_age)}` : '', max_age ? convertStoredAgeUnits(max_age) : ''];
};
const convertAgeAndUnitsToNumOfMonths = (age_count, age_units) => {
  if (age_units?.toLowerCase() === 'years') {
    return parseInt(age_count, 10) * 12;
  }
  if (age_units?.toLowerCase() === 'months') {
    return parseInt(age_count, 10);
  }
  return null;
};
const convertAgeToApiAgeRange = (age_req_min, age_req_min_units, age_req_max, age_req_max_units) => {
  const parsedAgeReqMin = convertAgeAndUnitsToNumOfMonths(age_req_min, age_req_min_units);
  const parsedAgeReqMax = convertAgeAndUnitsToNumOfMonths(age_req_max, age_req_max_units);
  if (!parsedAgeReqMin && !parsedAgeReqMax) return null;
  if (!parsedAgeReqMin) return `...${parsedAgeReqMax + 1}`;
  if (!parsedAgeReqMax) return `${parsedAgeReqMin}...`;
  return `${parsedAgeReqMin}...${parsedAgeReqMax + 1}`;
};
const convertAgesForDisplay = (count, units) => {
  if (units === 'years' && count < 2) {
    return count * 12;
  }
  if (units === 'months' && count >= 24) {
    return count / 12;
  }
  return count;
};
const convertAgeUnitsForDisplay = (count, units) => {
  if (units?.toLowerCase() === 'years' && count < 2) {
    return 'months';
  }
  if (units?.toLowerCase() === 'months' && count >= 24) {
    return 'years';
  }
  return units;
};
const processAgeText = (age_min, age_min_units, age_max, age_max_units) => {
  if (aboveApiAgeTopLimit(age_min, age_min_units) || aboveApiAgeTopLimit(age_max, age_max_units)) {
    return '';
  }
  const age_min_converted = age_min ? convertAgesForDisplay(age_min, age_min_units) : '';
  const age_min_converted_units = age_min ? convertAgeUnitsForDisplay(age_min, age_min_units) : '';
  const age_max_converted = age_max ? convertAgesForDisplay(age_max, age_max_units) : '';
  const age_max_converted_units = age_max ? convertAgeUnitsForDisplay(age_max, age_max_units) : '';
  if (age_min_converted && age_max_converted && age_min_converted_units && age_max_converted_units) {
    return `${age_min_converted} ${age_min_converted_units} to ${age_max_converted} ${age_max_converted_units}`;
  }
  if (age_min_converted && age_min_converted_units) {
    return `${age_min_converted} ${age_min_converted_units} and over`;
  }
  if (age_max_converted && age_max_converted_units) {
    return `${age_max_converted} ${age_max_converted_units} and under`;
  }
  return '';
};
const agePreview = (age_min, age_min_units, age_max, age_max_units) => {
  const ageText = processAgeText(age_min, age_min_units, age_max, age_max_units);
  if (ageText) {
    return ageText;
  }
  return 'Age range will appear here';
};
const getMaximumAgeForMinRange = (_, age_min_units, age_max_count, age_max_units) => {
  let maxAge = upperAgeLimit(age_min_units);
  if (!age_max_count || !age_max_units) return maxAge;
  if (age_max_count && age_min_units.toLowerCase() === 'years') {
    maxAge = Math.min(age_max_units.toLowerCase() === 'years' ? age_max_count : Math.floor(age_max_count / 12), maxAge);
  } else if (age_max_count && age_min_units.toLowerCase() === 'months') {
    maxAge = Math.min(age_max_units.toLowerCase() === 'years' ? age_max_count * 12 : age_max_count, maxAge);
  }
  return maxAge;
};
const getMinimumAgeForMaxRange = (age_min_count, age_min_units, _, age_max_units) => {
  let minAge = 0;
  if (!age_min_count || !age_min_units) return minAge;
  if (age_min_count && age_max_units.toLowerCase() === 'years') {
    minAge = Math.max(age_min_units.toLowerCase() === 'years' ? age_min_count : Math.floor(age_min_count / 12), minAge);
  } else if (age_min_count && age_max_units === 'months') {
    minAge = Math.max(age_min_units.toLowerCase() === 'years' ? age_min_count * 12 : age_min_count, minAge);
  }
  return minAge;
};
const basicChecks = (age_count, age_units) => {
  if (!age_count && !age_units) return '';
  if (!age_count && age_units) return 'Please enter an age';
  if (!age_units) return 'Units required';
  if (aboveApiAgeTopLimit(age_count, age_units)) return 'Cannot exceed 24 months or 100 years';
  if (age_count < 0) return 'Must be above 0';
  return '';
};
const validateMinRange = (age_min_count, age_min_units, age_max_count, age_max_units) => {
  const outOfRangeOrNoUnits = basicChecks(age_min_count, age_min_units);
  if (outOfRangeOrNoUnits) return outOfRangeOrNoUnits;
  if (!age_min_count) return '';
  const maxValidAge = getMaximumAgeForMinRange(age_min_count, age_min_units, age_max_count, age_max_units);
  if (age_min_count > maxValidAge) return 'Age minimum must be lower than max value';
  return '';
};
const validateMaxRange = (age_min_count, age_min_units, age_max_count, age_max_units) => {
  const outOfRangeOrNoUnits = basicChecks(age_max_count, age_max_units);
  if (outOfRangeOrNoUnits) return outOfRangeOrNoUnits;
  if (!age_max_count) return '';
  const minValidAge = getMinimumAgeForMaxRange(age_min_count, age_min_units, age_max_count, age_max_units);
  if (age_max_count < minValidAge) return 'Age maximum must be larger than min value';
  return '';
};
export default {
  upperAgeLimit,
  convertApiAgeRangeToUnits,
  convertAgeToApiAgeRange,
  agePreview,
  processAgeText,
  validateMinRange,
  validateMaxRange
};
//# sourceMappingURL=ageRangeConverters.js.map